import React, { useState, useEffect, FC } from 'react'
import styled from 'styled-components'
import Image from 'next/image'
import { ButtonDS, AccordionDS } from '../'
import {
    JotaSocialYoutube as YoutubeIcon,
    JotaSocialFacebook as FacebookIcon,
    JotaSocialInstagram as InstagramIcon,
    JotaSocialLinkedin as LinkedinIcon,
    JotaSocialTwitter as TwitterIcon,
    JotaSocialSpotifyPodcast as SpotifyPodcastIcon,
    tokens,
    JotaMaterialOpenNew
} from '@JOTAJornalismo/jota-design-system'
import { FooterIH } from '../../interfaces'
import { AUTH_CONSTANT, LAYOUT_CONSTANT } from '../../utils/constants'
import { useSelector } from 'react-redux'
import { StoreType } from '@App/api/types/store'
import staticFooterMenu from './footerlinks'
import { OUR_MISSION } from '@App/libs/utils/constants/global'
const { colors, fonts, spaces } = tokens

/**
 * Footer Component
 * @return {JSX.Element}
 */
export const { SM_PX, MD_PX, LG_PX } = LAYOUT_CONSTANT.BREAKPOINTS

type staticMenuType = {
    columnTitle: string
    columnItens: columnItensType[]
}

type columnItensType = {
    title: string
    url: string
    openInNewTab: boolean
}

const desktopItems = (staticMenu: staticMenuType[]) => {
    return staticMenu.map((column, index) => (
        <LinksContainer key={'desktop-' + index}>
            <Title>{column.columnTitle}</Title>
            {column.columnItens.map((columnItem) => (
                <Item
                    key={'desktop-' + columnItem.title}
                    data-test='footer-open-new-tab'
                    href={columnItem.url}
                >
                    {columnItem.title}
                    {columnItem.openInNewTab ? (
                        <JotaMaterialOpenNew
                            fill={colors.transparent.jotaTBlack24}
                        />
                    ) : (
                        ''
                    )}
                </Item>
            ))}
        </LinksContainer>
    ))
}

const mobileItems = (staticMenu: staticMenuType[]) => {
    return staticMenu.map((column, index) => (
        <MobileItems key={'mobile' + index}>
            <AccordionDS
                id={column.columnTitle}
                label={column.columnTitle}
                type='first'
                hasBorder
                RightIcon={true}
                onClick={() => {
                    const childs = document.getElementsByClassName(
                        column.columnTitle
                    ) as HTMLCollectionOf<HTMLElement>

                    if (childs[0].style.display == 'block') {
                        childs[0].style.display = 'none'
                    } else {
                        childs[0].style.display = 'block'
                    }
                    return {}
                }}
            />
            <AccordionContainer className={column.columnTitle}>
                {column.columnItens.map((item, itemIndex) => (
                    <AccordionDS
                        key={item.title + itemIndex}
                        id={item.title}
                        label={item.title}
                        type='second'
                        hasBorder
                        RightIcon={
                            item.openInNewTab ? 'jotaMaterialOpenNew' : ''
                        }
                        onClick={item.url}
                    />
                ))}
            </AccordionContainer>
        </MobileItems>
    ))
}

const AccordionContainer = styled.div`
    display: none;
    width: 100%;

    & > div {
        box-shadow: none;
    }
`

const FooterComponent: FC<{ footer: FooterIH }> = ({
    footer
}): React.JSX.Element => {
    const [windowWidth, setWindowWidth] = useState<any>()
    const { auth } = useSelector((state: StoreType) => state)
    const handleResize = () => {
        setWindowWidth(window.innerWidth)
    }

    useEffect(() => {
        handleResize()
        window.addEventListener('resize', () => handleResize())
    }, [footer.menu])

    return (
        <FooterContainer>
            <GrayContainer>
                <CTA id='footer-cta-container'>
                    <a href='https://www.jota.info'>
                        <Image
                            src={`${process.env.NEXT_PUBLIC_JU}/images/logo-jota-121.svg`}
                            alt='jota'
                            width={121}
                            height={45}
                        />
                    </a>
                    <Paragraph
                        isNotLogged={
                            auth?.authState !=
                            AUTH_CONSTANT.AUTH_STATE.USER_LOGGED
                        }
                    >
                        {OUR_MISSION}
                    </Paragraph>
                    {auth?.authState !=
                        AUTH_CONSTANT.AUTH_STATE.USER_LOGGED && (
                        <a
                            href={`${footer.url}`}
                            target='_blank'
                            rel='noreferrer'
                        >
                            <ButtonDS
                                id='jota-pro-footer'
                                label={footer.cta}
                                size='medium'
                                color='black'
                                width={
                                    windowWidth > 1199 || windowWidth < 500
                                        ? '100%'
                                        : 'auto'
                                }
                                type='filled'
                            />
                        </a>
                    )}
                </CTA>
                <Sitemap id='sitemap-editorials'>
                    {windowWidth > 1200 ? (
                        <Section data-test='sitemap-section'>
                            {desktopItems(staticFooterMenu)}
                        </Section>
                    ) : (
                        <Section data-test='sitemap-section'>
                            {mobileItems(staticFooterMenu)}
                        </Section>
                    )}
                </Sitemap>
            </GrayContainer>
            <BlackContainer>
                <List>
                    <ListItem>
                        <ListItemLink href={footer.paginaFAQ} target='_blank'>
                            FAQ{' '}
                            <JotaMaterialOpenNew
                                fill={colors.transparent.jotaTWhite50}
                            />
                        </ListItemLink>
                        <Separator>|</Separator>
                    </ListItem>
                    <ListItem>
                        <ListItemLink
                            href={footer.paginaContato}
                            target='_blank'
                        >
                            Contato{' '}
                            <JotaMaterialOpenNew
                                fill={colors.transparent.jotaTWhite50}
                            />
                        </ListItemLink>
                        <Separator>|</Separator>
                    </ListItem>
                    <ListItem>
                        <ListItemLink
                            href={footer.paginaTrabalhe}
                            target='_blank'
                        >
                            Trabalhe Conosco{' '}
                            <JotaMaterialOpenNew
                                fill={colors.transparent.jotaTWhite50}
                            />
                        </ListItemLink>
                    </ListItem>
                </List>
                {windowWidth <= 1200 && (
                    <Follow style={{ margin: 0 }}>Siga o Jota</Follow>
                )}
                <IconContainer>
                    {windowWidth > 1200 && (
                        <Follow style={{ margin: 0 }}>Siga o Jota</Follow>
                    )}
                    <a
                        href={`${footer.paginaRedeYoutube}`}
                        target='_blank'
                        rel='noreferrer'
                    >
                        <YoutubeIcon type='white' />
                    </a>

                    <a
                        href={`${footer.paginaRedeFacebook}`}
                        target='_blank'
                        rel='noreferrer'
                    >
                        <FacebookIcon type='white' />
                    </a>

                    <a
                        href={`${footer.paginaRedeInstagram}`}
                        target='_blank'
                        rel='noreferrer'
                    >
                        <InstagramIcon type='white' />
                    </a>

                    <a
                        href={`${footer.paginaRedeLinkedIn}`}
                        target='_blank'
                        rel='noreferrer'
                    >
                        <LinkedinIcon type='white' />
                    </a>

                    <a
                        href={`${footer.paginaRedeTwitter}`}
                        target='_blank'
                        rel='noreferrer'
                    >
                        <TwitterIcon type='white' />
                    </a>
                    <a
                        href={`${footer.paginaRedeSpotify}`}
                        target='_blank'
                        rel='noreferrer'
                    >
                        <SpotifyPodcastIcon type='white' />
                    </a>
                </IconContainer>
            </BlackContainer>
        </FooterContainer>
    )
}

const MobileItems = styled.div`
    width: 100%;
`

const FooterContainer = styled.footer`
    display: flex;
    flex-direction: column;
`
const GrayContainer = styled.div`
    padding: 44px;
    display: flex;
    justify-content: space-between;
    background-color: ${colors.transparent.jotaTGrayLight40};

    @media only screen and (max-width: ${MD_PX}) {
        flex-direction: column;
        padding: 44px 36px;
    }

    @media only screen and (max-width: ${SM_PX}) {
        flex-direction: column;
        padding: 44px 16px;
    }
`
const CTA = styled.div`
    max-width: 220px;

    @media only screen and (max-width: ${MD_PX}) {
        max-width: 100%;
    }

    @media only screen and (max-width: ${SM_PX}) {
        max-width: 100%;
    }
`
const Paragraph = styled.p<{ isNotLogged: boolean }>`
    ${fonts.jotaBody()};
    color: ${colors.gray.jotaGrayDark};
    padding: ${(props) => (props.isNotLogged ? '24px 0 44px 0' : '24px 0 0 0')};
    margin: 0;
`
const Sitemap = styled.div`
    display: flex;
    justify-content: space-between;

    @media only screen and (max-width: ${MD_PX}) {
        flex-direction: column;
    }

    @media only screen and (max-width: ${SM_PX}) {
        flex-direction: column;
    }
`
const Section = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: flex-start;
    gap: 50px;
    max-height: 380px;

    @media only screen and (max-width: ${MD_PX}) {
        flex-direction: column;
        display: flex;
        width: 100%;
        max-height: inherit;
        gap: 0;
    }
`

const LinksContainer = styled.div``

const Title = styled.h6`
    ${fonts.jotaOverline()};
    color: ${colors.gray.jotaGrayDarker};
    margin: 0;
    padding-bottom: 16px;
`
const Item = styled.a`
    display: flex;
    align-items: center;
    margin: 0;
    margin-bottom: 16px;
    ${fonts.jotaBodySmall};
    color: ${colors.gray.jotaGrayDarker};

    &:hover {
        cursor: pointer;
        text-decoration: underline;
    }

    & > svg {
        width: 16px;
        margin-left: 6px;
        height: 16px;
    }
`
const BlackContainer = styled.div`
    padding: ${spaces.jotaSpacing24} ${spaces.jotaSpacing44};
    background-color: ${colors.black.jotaBlackLight};
    display: flex;
    justify-content: space-between;
    align-items: center;

    @media only screen and (max-width: ${MD_PX}) {
        flex-direction: column;
        align-items: flex-start;
        gap: 36px;
        padding: ${spaces.jotaSpacing24} ${spaces.jotaSpacing36};
    }

    @media only screen and (max-width: ${SM_PX}) {
        flex-direction: column;
        align-items: flex-start;
        gap: 36px;
        padding: ${spaces.jotaSpacing24} ${spaces.jotaSpacing16};
    }
`
const List = styled.ul`
    display: flex;
    margin: 0;
    padding: 0;
`
const ListItem = styled.li`
    list-style-type: none;
    display: flex;
    align-items: center;
`
const ListItemLink = styled.a`
    ${fonts.jotaBody()};
    color: ${colors.white.jotaWhite};
    text-decoration: none;
    display: flex;
    align-items: center;

    &:hover {
        cursor: pointer;
        text-decoration: underline;
    }

    & > svg {
        width: 16px;
        margin-left: 6px;
        height: 16px;
    }
`
const Separator = styled.span`
    ${fonts.jotaBody()};
    color: ${colors.transparent.jotaTGrayLight40};
    margin: 0;
    padding: 0 12px;
`

const Follow = styled.p`
    ${fonts.jotaOverline()};
    color: ${colors.transparent.jotaTWhite50};
    margin: 0;
    padding: 0;
`

const IconContainer = styled.div`
    ${fonts.jotaOverline()};
    color: ${colors.transparent.jotaTWhite50};
    margin: 0;
    padding: 0;
    display: flex;
    gap: 24px;
    align-items: center;
    cursor: pointer;

    & > p {
        cursor: none;
    }
`

export default FooterComponent
